import _extends from "@babel/runtime/helpers/extends";
import _toConsumableArray from "@babel/runtime/helpers/toConsumableArray";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
import React, { useEffect, useRef, useState } from 'react';
import { useLazyQuery } from '@apollo/client';
import { CONSTANTS, getOneGraphQLClient, Rio, setCustomHeader } from '@chegg-plat-ui/utils';
import { Autocomplete } from '@chegg-ui/autocomplete';
import { Input } from '@chegg-ui/input';
import { CancelIcon, colors, LoadingIcon } from '@chegg-ui/theme';
import { debounce } from '@chegg-ui/utils';
import { PLZN_SEARCH_SCHOOL } from '../shared';
import { IconButtonWrapper, LoadingWrapper, NotListedListboxOptionWrapper, SchoolDescription, SchoolsDropdownWrapper, SearchIconWrapper } from '../styles';
var ERROR_MESSAGES = CONSTANTS.ERROR_MESSAGES,
    MANUAL_INPUT_CHAR_LIMIT = CONSTANTS.MANUAL_INPUT_CHAR_LIMIT,
    RIO_SCHOOL_NOT_LISTED_VALUE = CONSTANTS.RIO_SCHOOL_NOT_LISTED_VALUE,
    SCHOOL_NOT_FOUND_SELECTED_VALUE = CONSTANTS.SCHOOL_NOT_FOUND_SELECTED_VALUE,
    SCHOOL_NOT_LISTED_ID = CONSTANTS.SCHOOL_NOT_LISTED_ID,
    SCHOOL_NOT_LISTED_TEXT = CONSTANTS.SCHOOL_NOT_LISTED_TEXT,
    SEARCH_ROWS = CONSTANTS.SEARCH_ROWS;

var SchoolsDropdown = function SchoolsDropdown(_ref) {
  var className = _ref.className,
      defaultOptions = _ref.defaultOptions,
      _ref$defaultValue = _ref.defaultValue,
      defaultValue = _ref$defaultValue === void 0 ? '' : _ref$defaultValue,
      _ref$includeNotListed = _ref.includeNotListed,
      includeNotListed = _ref$includeNotListed === void 0 ? true : _ref$includeNotListed,
      _ref$labelText = _ref.labelText,
      labelText = _ref$labelText === void 0 ? 'Find my school' : _ref$labelText,
      _ref$manualInput = _ref.manualInput,
      manualInput = _ref$manualInput === void 0 ? {} : _ref$manualInput,
      onInputBlur = _ref.onInputBlur,
      _onInputChange = _ref.onInputChange,
      onInputFocus = _ref.onInputFocus,
      _onSelect = _ref.onSelect,
      _onSubmit = _ref.onSubmit,
      _ref$placeholder = _ref.placeholder,
      placeholder = _ref$placeholder === void 0 ? 'Ex: University of Cali...' : _ref$placeholder,
      _ref$showManualInput = _ref.showManualInput,
      showManualInput = _ref$showManualInput === void 0 ? true : _ref$showManualInput,
      _ref$width = _ref.width,
      width = _ref$width === void 0 ? '300px' : _ref$width,
      _ref$schoolNotFoundSe = _ref.schoolNotFoundSelectedValue,
      schoolNotFoundSelectedValue = _ref$schoolNotFoundSe === void 0 ? SCHOOL_NOT_FOUND_SELECTED_VALUE : _ref$schoolNotFoundSe,
      _ref$schoolNotListedT = _ref.schoolNotListedText,
      schoolNotListedText = _ref$schoolNotListedT === void 0 ? SCHOOL_NOT_LISTED_TEXT : _ref$schoolNotListedT,
      restProps = _objectWithoutProperties(_ref, ["className", "defaultOptions", "defaultValue", "includeNotListed", "labelText", "manualInput", "onInputBlur", "onInputChange", "onInputFocus", "onSelect", "onSubmit", "placeholder", "showManualInput", "width", "schoolNotFoundSelectedValue", "schoolNotListedText"]);

  var client = getOneGraphQLClient();
  setCustomHeader('apollographql-client-name', 'PLZN');

  var _useLazyQuery = useLazyQuery(PLZN_SEARCH_SCHOOL, process.env.JEST_WORKER_ID === undefined ?
  /* istanbul ignore next */
  {
    client: client
  } : {}),
      _useLazyQuery2 = _slicedToArray(_useLazyQuery, 2),
      searchSchool = _useLazyQuery2[0],
      _useLazyQuery2$ = _useLazyQuery2[1],
      loading = _useLazyQuery2$.loading,
      data = _useLazyQuery2$.data;

  var _useState = useState(defaultValue),
      _useState2 = _slicedToArray(_useState, 2),
      rioMetadataTerm = _useState2[0],
      setRioMetadataTerm = _useState2[1];

  var _useState3 = useState(manualInput.defaultValue || ''),
      _useState4 = _slicedToArray(_useState3, 2),
      manualSchoolInput = _useState4[0],
      setManualSchoolInput = _useState4[1];

  var _useState5 = useState(''),
      _useState6 = _slicedToArray(_useState5, 2),
      manualSchoolInputError = _useState6[0],
      setManualSchoolInputError = _useState6[1];

  var _useState7 = useState(),
      _useState8 = _slicedToArray(_useState7, 2),
      selectedSchool = _useState8[0],
      setSelectedSchool = _useState8[1];

  var _useState9 = useState(defaultValue),
      _useState10 = _slicedToArray(_useState9, 2),
      inputValue = _useState10[0],
      setInputValue = _useState10[1];

  var fetchSchool = useRef(debounce(function (value) {
    if (value.trim()) {
      searchSchool({
        variables: {
          query: value.trim(),
          limit: SEARCH_ROWS
        }
      });
    }
  }, 300));
  var schoolManualInputRef = useRef(null);
  var schoolDropdownInputRef = useRef(null);
  var notListedListboxOption = {
    uuid: SCHOOL_NOT_LISTED_ID,
    value: schoolNotFoundSelectedValue,
    child: /*#__PURE__*/React.createElement(NotListedListboxOptionWrapper, {
      isSelected: (selectedSchool === null || selectedSchool === void 0 ? void 0 : selectedSchool.uuid) === SCHOOL_NOT_LISTED_ID
    }, schoolNotListedText)
  };
  useEffect(function () {
    if (defaultValue === SCHOOL_NOT_LISTED_TEXT) {
      setSelectedSchool(notListedListboxOption);
    }
  }, []);
  var loadingListboxOption = {
    uuid: '',
    value: '',
    child: /*#__PURE__*/React.createElement(LoadingWrapper, {
      "data-test": "loading"
    }, /*#__PURE__*/React.createElement(LoadingIcon, {
      size: "small",
      svgColor: colors.cheggOrange
    }))
  };
  var dataListboxOptions = data && data.plznSchools && data.plznSchools.map(function (school) {
    return {
      uuid: school.id,
      value: school.institution,
      child: /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("strong", null, school.institution), /*#__PURE__*/React.createElement(SchoolDescription, null, "".concat(school.city, ", ").concat(school.state)))
    };
  });

  if (!dataListboxOptions) {
    dataListboxOptions = defaultOptions || [];
  }
  /* istanbul ignore next */
  else if (includeNotListed) {
      dataListboxOptions = [].concat(_toConsumableArray(dataListboxOptions), [notListedListboxOption]);
    }

  var schools = loading ? [loadingListboxOption] : dataListboxOptions;

  var onEnterAndSpaceKeyDown = function onEnterAndSpaceKeyDown(e, callback) {
    switch (e.key) {
      case 'Enter':
      case ' ':
      case 'Space':
        callback();
        break;

      default:
        return;
    }
  };

  return /*#__PURE__*/React.createElement(SchoolsDropdownWrapper, {
    isLoading: loading,
    className: className || '',
    width: width
  }, /*#__PURE__*/React.createElement(Autocomplete, _extends({}, restProps, {
    labelText: labelText,
    options: schools,
    placeholder: placeholder,
    onClick: function onClick() {
      Rio.sendClickStreamInteraction('start', {
        name: 'search school',
        text: inputValue,
        type: 'text'
      });
    },
    onInputBlur: onInputBlur,
    onInputChange: function onInputChange(value) {
      setInputValue(value);
      setRioMetadataTerm(value);
      fetchSchool.current(value);

      if (!value) {
        setSelectedSchool(undefined);
      }

      _onInputChange && _onInputChange(value);
    },
    onInputFocus: onInputFocus,
    onSelect: function onSelect(i) {
      setInputValue(schools[i].value);
      setSelectedSchool(schools[i]);
      /* istanbul ignore next */

      Rio.sendClickStreamInteraction('select', {
        name: 'school option',
        text: schools[i].value,
        type: 'drop down',
        value: schools[i].value === SCHOOL_NOT_LISTED_TEXT ? RIO_SCHOOL_NOT_LISTED_VALUE : schools[i].uuid
      }, {
        term: rioMetadataTerm,
        page_size: schools.length - 1
      });
      _onSelect && _onSelect(schools[i], schools);
    },
    onSubmit: function onSubmit(event) {
      _onSubmit && _onSubmit(event);
    },
    ref: schoolDropdownInputRef,
    trailingIcon: inputValue !== '' ? /*#__PURE__*/React.createElement(IconButtonWrapper, {
      "data-test": "".concat(restProps.inputId, "_clear"),
      onClick: function onClick() {
        setInputValue('');
        schoolDropdownInputRef.current && schoolDropdownInputRef.current.focus();
        setSelectedSchool(undefined);
        _onInputChange && _onInputChange('');
      },
      onKeyDown: function onKeyDown(event) {
        onEnterAndSpaceKeyDown(event, function () {
          setInputValue('');
          schoolDropdownInputRef.current && schoolDropdownInputRef.current.focus();
          setSelectedSchool(undefined);
          _onInputChange && _onInputChange('');
        });
      },
      screenReaderOnlyText: "Clear School Dropdown Input",
      size: "small"
    }, /*#__PURE__*/React.createElement(CancelIcon, {
      svgColor: colors.grey
    })) : /*#__PURE__*/React.createElement(SearchIconWrapper, {
      "data-test": "".concat(restProps.inputId, "_search"),
      size: "small",
      svgColor: colors.grey
    }),
    trailingIconClickable: true,
    value: inputValue
  })), showManualInput && selectedSchool && selectedSchool.uuid === SCHOOL_NOT_LISTED_ID ? /*#__PURE__*/React.createElement(Input, {
    "data-test": "manual_".concat(restProps.inputId),
    errorText: manualInput.errorText || manualSchoolInputError,
    inputId: "manual-".concat(restProps.inputId),
    labelText: manualInput.labelText || 'School name',
    onBlur: function onBlur(event) {
      manualInput.onBlur && manualInput.onBlur(event.target.value, SCHOOL_NOT_LISTED_ID);
    },
    onChange: function onChange(v) {
      if (v.length > MANUAL_INPUT_CHAR_LIMIT) {
        setManualSchoolInputError(ERROR_MESSAGES.MANUAL_SCHOOL_INPUT_SIZE);
        Rio.sendClickStreamNotification({
          type: 'error',
          message_name: ERROR_MESSAGES.RIO_ERROR_MESSAGE,
          message_detail: ERROR_MESSAGES.MANUAL_SCHOOL_INPUT_SIZE,
          element: {
            value: 'school',
            name: ERROR_MESSAGES.RIO_ERROR_MESSAGE,
            type: 'text'
          }
        });
      } else {
        setManualSchoolInputError('');
      }

      setManualSchoolInput(v);
      manualInput.onChange && manualInput.onChange(v, SCHOOL_NOT_LISTED_ID);
    },
    onFocus: function onFocus(event) {
      manualInput.onFocus && manualInput.onFocus(event.target.value, SCHOOL_NOT_LISTED_ID);
    },
    placeholder: manualInput.placeholder || 'Ex. University of Cali...',
    ref: schoolManualInputRef,
    trailingIcon: manualSchoolInput !== '' ? /*#__PURE__*/React.createElement(IconButtonWrapper, {
      "data-test": "manual_".concat(restProps.inputId, "_clear"),
      size: "small",
      screenReaderOnlyText: "Clear School Manual Input",
      onClick: function onClick() {
        setManualSchoolInput('');
        schoolManualInputRef.current && schoolManualInputRef.current.focus();
        manualInput.onChange && manualInput.onChange('', SCHOOL_NOT_LISTED_ID);
      },
      onKeyDown: function onKeyDown(event) {
        onEnterAndSpaceKeyDown(event, function () {
          setManualSchoolInput('');
          schoolManualInputRef.current && schoolManualInputRef.current.focus();
          manualInput.onChange && manualInput.onChange('', SCHOOL_NOT_LISTED_ID);
        });
      }
    }, /*#__PURE__*/React.createElement(CancelIcon, {
      svgColor: colors.grey
    })) : undefined,
    trailingIconClickable: true,
    type: "text",
    value: manualSchoolInput
  }) : null);
};

SchoolsDropdown.displayName = 'SchoolsDropdown';
export default SchoolsDropdown;